import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";

import { useBulkEmailChildren } from "@/admin/hooks/use-claims";
import { Button } from "@/shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { toastApiError } from "@/shared/lib/api-error";
import { useForm, type FormField } from "@/shared/lib/forms";

const FORM_ID = "bulk-email-form";
const plural = (n: number) => `${n} contact${n !== 1 ? "s" : ""}`;

/**
 * Sends the same customer-visible message to the consumer on every line of
 * a grouped claim. Mirrors ContactCustomerDialog but fans out
 * to all children via POST /claims/claims/<uuid>/bulk-email/.
 */
export function BulkEmailDialog({
  open,
  onOpenChange,
  parentUuid,
  recipientCount,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  parentUuid: string;
  recipientCount: number;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[480px]">
        {/* Mounted per opening, so each opening starts with an empty message. */}
        <BulkEmailForm
          parentUuid={parentUuid}
          recipientCount={recipientCount}
          onClose={() => onOpenChange(false)}
        />
      </DialogContent>
    </Dialog>
  );
}

function BulkEmailForm({
  parentUuid,
  recipientCount,
  onClose,
}: {
  parentUuid: string;
  recipientCount: number;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const bulkEmail = useBulkEmailChildren();
  const fields: FormField[] = [
    {
      type: "textarea",
      name: "message",
      label: "Message",
      value: "",
      placeholder: "Write the update you want every homeowner to receive...",
      required: "Write the message to send.",
      validationSchema: z
        .string()
        .trim()
        .min(1, "Write the message to send.")
        .min(10, "The message needs at least 10 characters."),
    },
  ];
  const { Form } = useForm(
    { name: FORM_ID, submit: "footer", fields },
    async (values) => {
      try {
        const result = await bulkEmail.mutateAsync({
          parentUuid,
          message: String(values.message ?? "").trim(),
        });
        const sent = result?.sent_count ?? 0;
        const skipped = result?.skipped_count ?? 0;
        toast.success(
          skipped > 0
            ? `Sent to ${plural(sent)} (${skipped} skipped - no email)`
            : `Sent to ${plural(sent)}`,
        );
        onClose();
      } catch (err: unknown) {
        toastApiError(err, "Failed to send bulk message");
      }
    },
    [parentUuid],
  );

  return (
    <>
      <DialogHeader>
        <DialogTitle className="text-[15px] font-normal text-[var(--ink)]">
          {t("claims.bulkEmail.title", "Email Contacts")}
        </DialogTitle>
        <DialogDescription className="text-[12px] text-[var(--ink-3)]">
          {t("claims.bulkEmail.description", {
            count: recipientCount,
            defaultValue_one:
              "Send the same message to the owner of every line on this claim ({{count}} contact). It appears in each line's claim history and is emailed to its owner.",
            defaultValue_other:
              "Send the same message to the owner of every line on this claim ({{count}} contacts). It appears in each line's claim history and is emailed to its owner.",
          })}
        </DialogDescription>
      </DialogHeader>
      <div className="mt-3">
        <Form className="flex flex-col gap-4" />
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onClose} className="cursor-pointer">
          Cancel
        </Button>
        <Button
          type="submit"
          form={FORM_ID}
          disabled={bulkEmail.isPending}
          className="cursor-pointer"
        >
          {bulkEmail.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          {t("claims.bulkEmail.send", {
            count: recipientCount,
            defaultValue_one: "Send to {{count}} contact",
            defaultValue_other: "Send to {{count}} contacts",
          })}
        </Button>
      </DialogFooter>
    </>
  );
}
